import React, {FC, useEffect, useMemo, useState} from 'react';
import {Button, Flex, PriorityIcon} from '@priority-software/priority-style-react';
import texts from '../../texts';
import classNames from 'classnames';
import styles from './Footer.module.scss';
import {NextState, WizardStepEnum} from '../../types';
import {useFooterContext, useSideBarContext} from '../../hooks';
import {isRTL} from '../../../../Localization';
import {updateStepStatus} from '../../actions/wizardLog';
import {AssignStepDailog} from './AssignStepDialog';
import {setReminder} from '../../actions/reminders';
import {getUsername} from '../../../../_shared/GWTBridge/invoke/genPage';
import moment from 'moment';

export const Footer: FC = () => {
    const [assignStepDialogOpen, setAssignStepDialogOpen] = useState(false);
    const [blockedNextButton, setBlockedNextButton] = useState(false);
    const footerState = useFooterContext();
    const sideBarState = useSideBarContext();
    const {owner, stepResponsible} = sideBarState;
    const isRtl = isRTL();

    useEffect(() => {
        if (
            footerState.fiscalYearEndDate === undefined &&
            sideBarState.currentStepIndex ===
                sideBarState.stageStepsMapping[sideBarState.currentStageIndex].steps.length - 1 &&
            sideBarState.stageStepsMapping.length - 1 !== sideBarState.currentStageIndex
        ) {
            setBlockedNextButton(true);
        } else {
            setBlockedNextButton(false);
        }
    }, [
        footerState.fiscalYearEndDate,
        sideBarState.currentStepIndex,
        sideBarState.currentStageIndex
    ]);

    const defaultNextButtonText = useMemo(() => {
        switch (sideBarState.currentStep.type) {
            case WizardStepEnum.Welcome:
                if (sideBarState.startStepIndex > 1) {
                    return texts.continue_label;
                }
                return texts[sideBarState.currentStep.next_button];
            case WizardStepEnum.Common:
                return sideBarState.currentStepIndex <
                    sideBarState.stageStepsMapping[sideBarState.currentStageIndex].steps.length - 2
                    ? texts['next']
                    : texts['done'];
            case WizardStepEnum.Finished:
                return texts['done'];
        }
    }, [sideBarState]);

    useEffect(() => {
        const moveToNextStep = async () => {
            if (footerState.onNextState === NextState.Done) {
                footerState.setOnNextState(NextState.Idle);
                // Updating step status without awaiting
                // to prevent blocking the flow
                // a separate session is started for the updateStepStatus
                // and no further actions are dependent on it
                updateStepStatus(
                    sideBarState.stageStepsMapping[sideBarState.currentStageIndex]?.steps[
                        sideBarState.currentStepIndex
                    ].logId,
                    owner
                );

                if (
                    owner !== stepResponsible &&
                    owner !== getUsername() &&
                    sideBarState.currentStepIndex !== 0
                ) {
                    footerState.onStepComplete();
                    return;
                }

                if (sideBarState.currentStep.type === WizardStepEnum.Welcome) {
                    sideBarState.setCurrentStepIndex(sideBarState.startStepIndex);
                } else if (
                    sideBarState.currentStepIndex <
                    sideBarState.stageStepsMapping[sideBarState.currentStageIndex].steps.length - 1
                ) {
                    sideBarState.setCurrentStepIndex(sideBarState.currentStepIndex + 1);
                } else {
                    if (
                        sideBarState.currentStageIndex <
                            sideBarState.stageStepsMapping.length - 1 &&
                        moment(footerState.fiscalYearEndDate, 'MM/DD/YYYY').diff(
                            moment(),
                            'days'
                        ) <= 0
                    ) {
                        sideBarState.setCurrentStageIndex(sideBarState.currentStageIndex + 1);
                        sideBarState.setCurrentStepIndex(0);
                    } else {
                        footerState.onCloseCallback();
                    }
                }
            }
        };
        moveToNextStep();
    }, [footerState.onNextState, owner, stepResponsible]);

    return (
        <Flex
            direction={'row'}
            alignItems={'center'}
            justifyContent={
                sideBarState.currentStep.type !== WizardStepEnum.Finished
                    ? 'space-between'
                    : 'flex-end'
            }
            componentClasses={styles.footerWrapper}
        >
            {sideBarState.currentStep.type !== WizardStepEnum.Finished && (
                <Flex
                    direction={'row'}
                    alignItems={'center'}
                    justifyContent={'flex-start'}
                    componentClasses={styles.buttonsWrapper}
                >
                    <Button
                        skin={'text'}
                        componentClasses={styles.textButton}
                        prefixIcon={PriorityIcon.clock}
                        size={'medium'}
                        onClick={() => {
                            setReminder(sideBarState.currentStep?.logId);
                            footerState.onCloseCallback();
                        }}
                    >
                        {texts['remind_later']}
                    </Button>
                    {sideBarState.currentStep.type !== WizardStepEnum.Welcome && (
                        <>
                            <Button
                                skin={'text'}
                                componentClasses={styles.textButton}
                                prefixIcon={PriorityIcon.newUser}
                                size={'medium'}
                                onClick={() => setAssignStepDialogOpen(true)}
                            >
                                {texts['assign_step_to']}
                            </Button>
                            <AssignStepDailog
                                isDialogOpen={assignStepDialogOpen}
                                onClose={() => setAssignStepDialogOpen(false)}
                                stepId={sideBarState.currentStep.logId}
                                zIndex={footerState.dialogZIndex}
                                closeWizard={footerState.onCloseCallback}
                            />
                        </>
                    )}
                </Flex>
            )}
            <Button
                size={'medium'}
                skin={sideBarState.currentStep.type === WizardStepEnum.Common ? 'text' : 'primary'}
                disabled={
                    (!footerState.isValid &&
                        sideBarState.currentStep.type !== WizardStepEnum.Welcome &&
                        sideBarState.currentStep.type !== WizardStepEnum.Finished) ||
                    blockedNextButton
                }
                componentClasses={classNames({
                    [styles.welcomeScreenButton]:
                        sideBarState.currentStep.type === WizardStepEnum.Welcome,
                    [styles.finishedScreenButton]:
                        sideBarState.currentStep.type === WizardStepEnum.Finished,
                    [styles.textButton]: sideBarState.currentStep.type === WizardStepEnum.Common
                })}
                suffixIcon={
                    sideBarState.currentStep.type === WizardStepEnum.Common
                        ? isRtl
                            ? PriorityIcon.arrowLeft
                            : PriorityIcon.arrowRight
                        : undefined
                }
                onClick={async () => {
                    footerState.setOnNextState(NextState.Processing);
                }}
            >
                {footerState.customNextLabel || defaultNextButtonText}
            </Button>
        </Flex>
    );
};
